// MTCompute - configuration-driven Compute tab (MTWHT-CMP-01 / PNW-1764).
// The workflow step after Prepare. Shows the computed Withholding Tax output for
// the selected entity/period: the PROCESSED transactions grouped and totalled per
// the country's Compute Configuration.
//
// - AC1 Filters: MTKeyFilterPanel (Entity, Entity TIN, Year single, Month multi) + View.
// - AC2 Grid columns are driven by the Compute Configuration (summary-config API):
//   dimensions (GROUP_BY) then measures (SUM), ACTIVE only, ordered by displayOrder.
// - AC3 A totals row is pinned at the top, summing each SUM column across the view.
// - AC4 Only PROCESSED transactions feed Compute.
// - AC5 Search/filter over the displayed columns via the shared Search popover.
// - AC6 Download exports the grid as Excel (binary .xlsx), honouring the filter/search.
// - AC7 Pagination via MTCommonTable footer (default 100).
// - AC8 Re-running View / paging / searching recomputes against current Processed data.
import DownloadIcon from '@/assets/icons/DownloadIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import { Search } from '@/components/common/index.js'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import type { MTKeyFilterViewData } from '@/multi-territory/mt-components/mt-shared/MTKeyFilterPanel'
import { MTKeyFilterPanel } from '@/multi-territory/mt-components/mt-shared/MTKeyFilterPanel'
import {
    MT_COMPUTE_BUCKET,
    downloadMTComputeExcel,
    fetchMTComputeConfig,
    fetchMTComputeData,
    fetchMTComputeTotalSum,
} from '@/multi-territory/mt-services/mt-compute'
import type {
    MTComputeField,
    MTComputeRequest,
    MTComputeTotalSum,
} from '@/multi-territory/mt-types/mt-compute'
import type { MTMetadataTableColumn } from '@/multi-territory/mt-types/mt-metadata'
import { sanitizeFilename } from '@/multi-territory/mt-utils/mt-sanitize'
import {
    formatMTHeaderLabel,
    formatMTSearchCriteria,
} from '@/multi-territory/mt-utils/mt-search-fields'
import { Box, IconButton, Toolbar, Tooltip, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

/** Marker key for the synthetic totals row rendered at the top of the grid. */
const TOTAL_ROW_FLAG = '__isTotal'

const isActiveField = (f: MTComputeField) =>
  !f.status || f.status.toUpperCase() === 'ACTIVE'

const MTCompute = () => {
  const { showSnackbar } = useSnackbar() as any
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country?.countryCode
  )

  const [configFields, setConfigFields] = useState<MTComputeField[]>([])
  const [sumFieldKeys, setSumFieldKeys] = useState<string[]>([])

  const [filterData, setFilterData] = useState<
    (MTKeyFilterViewData & { entityIdentifiers: string[] }) | null
  >(null)
  const [showTable, setShowTable] = useState(false)

  const [data, setData] = useState<Record<string, any>[]>([])
  const [totalSum, setTotalSum] = useState<MTComputeTotalSum | null>(null)
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)

  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    {
      id: 1,
      logicalOperator: 'Where',
      field: '',
      operator: 'EQUAL_TO',
      value: '',
    },
  ])
  const [searchList, setSearchList] = useState<any[]>([])

  // Load the Compute Configuration -> columns + SUM field keys (AC2)
  useEffect(() => {
    if (!countryCode) return
    setShowTable(false)
    setData([])
    setTotalSum(null)
    fetchMTComputeConfig(countryCode).then(res => {
      if (!res.success || !res.data) {
        setConfigFields([])
        setSumFieldKeys([])
        return
      }
      const dimensions = (res.data.dimensions ?? []).filter(isActiveField)
      const measures = (res.data.measures ?? []).filter(isActiveField)
      const ordered = [...dimensions, ...measures].sort(
        (a, b) => a.displayOrder - b.displayOrder
      )
      setConfigFields(ordered)
      setSumFieldKeys(measures.map(m => m.fieldKey))
    })
  }, [countryCode])

  const displayColumns = useMemo<MTMetadataTableColumn[]>(() => {
    const sumSet = new Set(sumFieldKeys)
    return configFields.map(f => {
      const isSum = sumSet.has(f.fieldKey)
      return {
        id: f.fieldKey,
        label: f.label,
        type: 'text',
        sortable: true,
        editable: false,
        // Column sizing (min/max width + wrapping) is owned by MTCommonTable.
        render: (value: any, row?: any) => {
          const isTotal = !!row?.[TOTAL_ROW_FLAG]
          if (isTotal) {
            // Totals row: bold sum under each SUM column only. No "Total" label
            // and nothing under the GROUP_BY columns (incl. the first/Year column).
            return isSum ? <strong>{formatNumber(value)}</strong> : ''
          }
          if (value === null || value === undefined || value === '') return '-'
          return String(value)
        },
      }
    })
  }, [configFields, sumFieldKeys])

  const searchFields = useMemo(
    () =>
      configFields.map(f => {
        // Match the advance-search label to the table header exactly
        // (sentence case / "running text", acronyms such as ID/TIN uppercase).
        const label = formatMTHeaderLabel(f.label)
        return {
          id: f.fieldKey,
          label,
          type: 'text',
          placeholder: `Enter ${label}`,
        }
      }) as any[],
    [configFields]
  )

  const tableData = useMemo(() => {
    if (!totalSum || data.length === 0) return data
    // Synthetic totals row: only the SUM values are populated; GROUP_BY columns
    // (incl. the first/Year column) are intentionally left blank.
    const totalsRow: Record<string, any> = { [TOTAL_ROW_FLAG]: true }
    sumFieldKeys.forEach(key => {
      totalsRow[key] = totalSum[key]
    })
    return [totalsRow, ...data]
  }, [data, totalSum, sumFieldKeys])

  const buildEntityIdentifiers = (fd: MTKeyFilterViewData): string[] =>
    fd.tanApplicable
      ? fd.tanNumber
        ? [fd.tanNumber]
        : []
      : fd.uniqueIdentificationNumber
        ? [fd.uniqueIdentificationNumber]
        : []

  const buildRequest = useCallback(
    (
      fd: {
        entityIdentifiers: string[]
        months: { year: number; month: number }[]
      },
      {
        page = 1,
        pageSize = itemsPerPage,
        search = searchList,
        isReportDownload = false,
      }: {
        page?: number
        pageSize?: number
        search?: any[]
        isReportDownload?: boolean
      } = {}
    ): MTComputeRequest => ({
      pageFilter: {
        currentPage: page,
        pageSize,
        sortField: null,
        sortOrder: 'ASC',
        includeCount: true,
      },
      searchCOList: search,
      entityIdentifiers: fd.entityIdentifiers,
      periodFilters: fd.months,
      isReportDownload,
    }),
    [itemsPerPage, searchList]
  )

  const loadCompute = useCallback(
    async ({
      page = 1,
      pageSize = itemsPerPage,
      search = searchList,
      fd = filterData,
    }: {
      page?: number
      pageSize?: number
      search?: any[]
      fd?: (MTKeyFilterViewData & { entityIdentifiers: string[] }) | null
    } = {}) => {
      if (!countryCode || !fd) return
      setLoading(true)
      const body = buildRequest(fd, { page, pageSize, search })
      try {
        const [dataRes, sumRes] = await Promise.all([
          fetchMTComputeData(countryCode, MT_COMPUTE_BUCKET, body),
          fetchMTComputeTotalSum(countryCode, MT_COMPUTE_BUCKET, body),
        ])
        if (dataRes.success) {
          setData(dataRes.data?.content ?? [])
          setTotalItems(dataRes.data?.totalElements ?? 0)
        } else {
          setData([])
          setTotalItems(0)
          showSnackbar(dataRes.error || 'Failed to load computed data', 'error')
        }
        setTotalSum(sumRes.success ? (sumRes.data ?? null) : null)
      } catch {
        showSnackbar('Failed to load computed data', 'error')
      } finally {
        setLoading(false)
      }
    },
    [
      countryCode,
      buildRequest,
      filterData,
      itemsPerPage,
      searchList,
      showSnackbar,
    ]
  )

  const handleView = useCallback(
    async (fd: MTKeyFilterViewData) => {
      if (!fd.months || fd.months.length === 0) {
        showSnackbar('Select Year and Month before viewing', 'info')
        return
      }
      const enriched = { ...fd, entityIdentifiers: buildEntityIdentifiers(fd) }
      setFilterData(enriched)
      setShowTable(true)
      setCurrentPage(1)
      await loadCompute({ page: 1, fd: enriched })
    },
    [loadCompute, showSnackbar]
  )

  const handlePageChange = async (page: number) => {
    setCurrentPage(page)
    await loadCompute({ page })
  }

  const handleItemsPerPageChange = async (size: number) => {
    setItemsPerPage(size)
    setCurrentPage(1)
    await loadCompute({ page: 1, pageSize: size })
  }

  const handleSearch = (e: React.MouseEvent<HTMLElement>) => {
    setSearchOpen(v => !v)
    setAnchorEl(e.currentTarget)
  }
  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }
  const handleApplySearch = async (criteria: any[]) => {
    const apiCriteria = formatMTSearchCriteria(criteria)
    const mapped = apiCriteria.map((item: any, idx: number) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: item.id || idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mapped)
    setSearchList(apiCriteria)
    setCurrentPage(1)
    await loadCompute({ page: 1, search: apiCriteria })
    setSearchOpen(false)
  }
  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: '',
        operator: 'EQUAL_TO',
        value: '',
      },
    ])
    setSearchList([])
    setCurrentPage(1)
    await loadCompute({ page: 1, search: [] })
    setSearchOpen(false)
  }

  // Download Excel (AC6) - the API streams the .xlsx binary directly, so we read
  // it as a Blob and trigger a browser download. No JSON parsing (the body starts
  // with "PK", the zip/xlsx magic bytes, and is NOT valid JSON).
  const handleDownload = async () => {
    if (!countryCode || !filterData) return
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    setDownloading(true)
    const body = buildRequest(filterData, { isReportDownload: true })
    try {
      const res = await downloadMTComputeExcel(
        countryCode,
        MT_COMPUTE_BUCKET,
        body
      )
      if (res.success) {
        triggerBlobDownload(res.data.blob, res.data.filename)
        showSnackbar('Download started', 'success')
      } else {
        showSnackbar(res.error || 'Download failed', 'error')
      }
    } catch {
      showSnackbar('Download failed', 'error')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* AC1 - Entity / Entity TIN / Year / Month + View */}
      <MTKeyFilterPanel onViewClick={handleView} showYear showMonth />

      {showTable && (
        <>
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              px: 3,
              py: 1,
              minHeight: '48px !important',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                color: 'var(--forbrand-secondary)',
                fontWeight: 500,
              }}
            >
              Compute
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Download Excel">
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: 'var(--forbrand-primary)' }}
                    onClick={handleDownload}
                    disabled={downloading}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Search">
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: 'var(--forbrand-primary)' }}
                    onClick={handleSearch}
                  >
                    <SearchIconBtn />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Toolbar>

          <Box>
            <MTCommonTable
              columns={displayColumns as any}
              data={tableData}
              loading={loading}
              showFooter
              emptyMessage="No computed data found"
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        </>
      )}

      {React.createElement(Search as any, {
        open: searchOpen,
        onClose: handleCloseSearch,
        onApply: handleApplySearch,
        onReset: handleResetSearch,
        anchorEl,
        fields: searchFields,
        placement: 'bottom-start',
        setSearchCriteria,
        searchCriteria,
      })}
    </Box>
  )
}

/** Format a numeric total for display; leaves non-numeric values as-is. */
function formatNumber(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  const num = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(num)) return String(value)
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Trigger a browser download for a Blob with the given filename. */
function triggerBlobDownload(blob: Blob, filename: string): void {
  // CWE-80: `filename` is untrusted (server Content-Disposition / parsed row
  // data). It is neutralized up front and the download anchor is NEVER inserted
  // into the document — we click a detached element — so no tainted value ever
  // reaches a `Node.appendChild` DOM sink.
  const safeFilename = sanitizeFilename(filename)
  const url = window.URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = safeFilename
    link.rel = 'noopener'
    // Detached click — no appendChild / removeChild on document.body.
    link.click()
  } finally {
    window.URL.revokeObjectURL(url)
  }
}

export default MTCompute
